export type AbVariant = {
  key: string;
  weight: number;
};

export type AbExperiment = {
  id: string;
  cookieName: string;
  variants: readonly AbVariant[];
  cookieMaxAgeSeconds: number;
};

export type ExperimentEventParams = {
  experiment_id?: string;
  variant?: string;
};

function getStorageValue(storage: Storage | undefined, key: string) {
  try {
    return storage?.getItem(key) || undefined;
  } catch {
    return undefined;
  }
}

function setStorageValue(storage: Storage | undefined, key: string, value: string) {
  try {
    storage?.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function getFallbackStorageKey(cookieName: string) {
  return `sr_ab:${cookieName}`;
}

function getCookieValue(name: string) {
  if (typeof document === "undefined") return undefined;

  const cookie = document.cookie.split("; ").find((row) => row.startsWith(`${encodeURIComponent(name)}=`));

  return cookie ? decodeURIComponent(cookie.split("=")[1] ?? "") : undefined;
}

function setCookieValue(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === "undefined") return;

  document.cookie = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    "Path=/",
    "SameSite=Lax",
  ].join("; ");
}

function isValidVariant(experiment: AbExperiment, value: string | undefined) {
  return experiment.variants.some((variant) => variant.key === value);
}

function pickWeightedVariant(experiment: AbExperiment) {
  const totalWeight = experiment.variants.reduce((sum, variant) => sum + variant.weight, 0);
  const randomValue = Math.random() * totalWeight;

  let cursor = 0;
  for (const variant of experiment.variants) {
    cursor += variant.weight;
    if (randomValue < cursor) return variant.key;
  }

  return experiment.variants[0]?.key ?? "control";
}

function getLocalForcedVariant(experiment: AbExperiment) {
  if (typeof window === "undefined") return undefined;
  if (!["localhost", "127.0.0.1"].includes(window.location.hostname)) return undefined;

  const searchParams = new URLSearchParams(window.location.search);
  const forcedVariant =
    searchParams.get("sr_ab_variant") ||
    searchParams.get(`sr_ab_${experiment.id}`) ||
    process.env.NEXT_PUBLIC_FORCE_AB_VARIANT;

  return isValidVariant(experiment, forcedVariant || undefined) ? forcedVariant || undefined : undefined;
}

export function getAssignedExperimentVariant(experiment: AbExperiment) {
  const forcedVariant = getLocalForcedVariant(experiment);
  if (forcedVariant) return forcedVariant;

  const savedVariant = getCookieValue(experiment.cookieName);
  if (isValidVariant(experiment, savedVariant)) return savedVariant;

  if (typeof window === "undefined") return undefined;
  const fallbackKey = getFallbackStorageKey(experiment.cookieName);
  const localVariant = getStorageValue(window.localStorage, fallbackKey);
  if (isValidVariant(experiment, localVariant)) return localVariant;
  const sessionVariant = getStorageValue(window.sessionStorage, fallbackKey);
  return isValidVariant(experiment, sessionVariant) ? sessionVariant : undefined;
}

export function getOrAssignExperimentVariant(experiment: AbExperiment) {
  const forcedVariant = getLocalForcedVariant(experiment);
  if (forcedVariant) {
    setCookieValue(experiment.cookieName, forcedVariant, experiment.cookieMaxAgeSeconds);
    const fallbackKey = getFallbackStorageKey(experiment.cookieName);
    setStorageValue(window.localStorage, fallbackKey, forcedVariant);
    setStorageValue(window.sessionStorage, fallbackKey, forcedVariant);
    return forcedVariant;
  }

  const savedVariant = getAssignedExperimentVariant(experiment);
  if (savedVariant) return savedVariant;

  const variant = pickWeightedVariant(experiment);
  setCookieValue(experiment.cookieName, variant, experiment.cookieMaxAgeSeconds);
  if (typeof window !== "undefined") {
    const fallbackKey = getFallbackStorageKey(experiment.cookieName);
    setStorageValue(window.localStorage, fallbackKey, variant);
    setStorageValue(window.sessionStorage, fallbackKey, variant);
  }
  return variant;
}

export function getExperimentEventParams(experiment: AbExperiment): ExperimentEventParams {
  const variant = getAssignedExperimentVariant(experiment);

  if (!variant) return {};

  return {
    experiment_id: experiment.id,
    variant,
  };
}
